import { PageBody, Spacer, Stack, TextTitle } from '@pypestream/design-system';
import { useTranslation } from '@pypestream/translations';
import {
  checkInGroups as checkInGroupsOf,
  groupSpec,
  groupsOnPage,
  SettingsFormProvider,
  SettingsGroupCard,
  type SettingsGroupCardCopy,
  type SettingsGroupView,
  type SettingsOverride,
  type SettingsSource,
  type SettingsValues,
} from '@pypestream/ui-patterns';
import { FC, useEffect, useState } from 'react';
import { useBlocker, useParams } from 'react-router';

import { Loader } from '../../../../components';
import { useManagerCtxSelector } from '../../../../xstate/app.xstate';
import { IPage } from '../../../types';
import {
  fetchOrgGroup,
  fetchOverriddenIn,
  MaestroError,
  reasonFor,
  resetOrgGroup,
  saveOrgGroup,
} from './maestro-org-api';
import { OrganizationTabs, OrgTab } from './organization-tabs';
import { overriddenInRows } from './overridden-in';
import { OverriddenInTable } from './overridden-in-table';
import {
  CheckInCard,
  CheckInTag,
  DefaultsBanner,
  NoAccessCard,
} from './org-tab-parts';
import { useLeafLabel } from './use-leaf-label';
import { useIsRootOrg } from './use-is-root-org';
import { useMaestroOrgPermissions } from './use-maestro-org-permissions';
import { maestroSourceHref } from './source-href';

export const SECTION_GROUPS = {
  'ai-models': groupsOnPage('org', 'ai'),
  voice: groupsOnPage('org', 'voice'),
};

/** Real groups the design has no card for: kept, and flagged for product. */
const CHECK_IN_GROUPS: readonly string[] = checkInGroupsOf();

type Section = keyof typeof SECTION_GROUPS;

interface GroupState {
  view: SettingsGroupView;
  overrides: SettingsOverride[];
  saving: boolean;
  error: MaestroError | null;
}

const loadGroup = async (orgId: string, group: string) => {
  const [view, overrides] = await Promise.all([
    fetchOrgGroup(orgId, group),
    fetchOverriddenIn(orgId, group),
  ]);
  return { view, overrides };
};

/** One section's groups. Mount it keyed by section: its loads belong to one section. */
export const MaestroOrgSettingsPage: FC<IPage & { section: Section }> = ({
  section,
}) => {
  const { t } = useTranslation();
  const labelFor = useLeafLabel();
  const { org_id: orgId = '' } = useParams();
  const orgName = useManagerCtxSelector(
    (ctx) => ctx.orgDetails?.name ?? ctx.childOrgs?.currentOrg?.name
  );
  const parentId = useManagerCtxSelector((ctx) =>
    ctx.orgDetails?.id === orgId ? ctx.orgDetails.parentId : undefined
  );
  const permissions = useMaestroOrgPermissions(orgId);
  const isRootOrg = useIsRootOrg(orgId);
  const [groups, setGroups] = useState<Record<string, GroupState>>({});
  const [loadError, setLoadError] = useState<MaestroError | null>(null);

  useEffect(() => {
    let current = true;
    setGroups({});
    setLoadError(null);
    Promise.all(
      SECTION_GROUPS[section].map(async (group) => {
        const loaded = await loadGroup(orgId, group);
        return [group, { ...loaded, saving: false, error: null }] as const;
      })
    ).then(
      (loaded) => current && setGroups(Object.fromEntries(loaded)),
      (error) => current && setLoadError(reasonFor(error).reason)
    );
    return () => {
      current = false;
    };
  }, [orgId, section]);

  const patch = (group: string, next: Partial<GroupState>) =>
    setGroups((existing) => ({
      ...existing,
      [group]: { ...existing[group], ...next },
    }));

  // Resolves once the card's new view is in state, so the card resets from it.
  const run = async (
    group: string,
    action: () => Promise<SettingsGroupView | void>
  ): Promise<boolean> => {
    patch(group, { saving: true, error: null });
    try {
      const view = (await action()) ?? (await fetchOrgGroup(orgId, group));
      patch(group, { view, saving: false });
      return true;
    } catch (error) {
      const { reason } = reasonFor(error);
      // A lost race adopts what the other editor saved, with its lock version;
      // the card rebases the user's own edits onto it so a retry can land.
      patch(group, { saving: false, error: reason });
      if (reason === 'stale') {
        await loadGroup(orgId, group).then(
          (loaded) => patch(group, loaded),
          () => undefined
        );
      }
      return false;
    }
  };

  const sourceName = (source: SettingsSource | null) =>
    source?.root || !source
      ? t('manager/organizations:orgTabs.platformDefault')
      : t('manager/organizations:orgTabs.orgDefault', {
          name: source.name ?? '',
        });

  const key = (name: string) => `manager/organizations:orgTabs.${name}`;
  const copy: SettingsGroupCardCopy = {
    sourceLabel: sourceName,
    sourceHref: (source) => maestroSourceHref(source, parentId, section),
    sourceTooltip: (source) =>
      t(key('sourceTooltip'), { source: sourceName(source) }),
    opensInNewTab: t(key('opensInNewTab')),
    resetLabel: (source) => t(key('resetTo'), { source: sourceName(source) }),
    overridden: t(key('status.overridden')),
    unsaved: t(key('status.unsaved')),
    moreAbout: (label) => t(key('moreAbout'), { label }),
    recommended: t(key('recommended')),
    save: t('manager/organizations:editOrg.save'),
    cancel: t('manager/organizations:editOrg.cancel'),
    notSet: t(key('notSet')),
    invalidValue: t(key('invalidValue')),
    off: t(key('off')),
    turnOn: (label) => t(key('turnOn'), { label }),
    changedElsewhere: (value) => t(key('changedElsewhere'), { value }),
  };

  const barCopy = {
    label: t(key('bar.label')),
    unsaved: t(key('bar.unsaved')),
    invalid: (count: number) => t(key('bar.invalid'), { count }),
    notSaved: (count: number) => t(key('bar.notSaved'), { count }),
    cancel: t('manager/organizations:editOrg.cancel'),
    save: t(key('bar.save')),
    leave: {
      title: t(key('bar.leave.title')),
      body: t(key('bar.leave.body')),
      confirm: t(key('bar.leave.confirm')),
      stay: t(key('bar.leave.stay')),
    },
  };

  const fieldText = (id: string) =>
    String(t(key(`fields.${id}`), { org: orgName ?? '', defaultValue: '' })) ||
    undefined;

  const card = (group: string, embedded: boolean) => {
    const state = groups[group];
    if (!state || !permissions) return null;
    const checkIn = CHECK_IN_GROUPS.includes(group);
    const description = [
      t(key(`descriptions.${group}`), { defaultValue: '' }),
      checkIn && !embedded && t(key('checkIn.body')),
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <SettingsGroupCard
        key={group}
        view={state.view}
        title={t(`manager/organizations:orgTabs.groups.${group}`)}
        titleAddon={checkIn && !embedded && <CheckInTag />}
        embedded={embedded}
        description={description || undefined}
        spec={groupSpec(group, labelFor, { text: fieldText })}
        canManage={permissions.canManageSettings}
        topmost={isRootOrg}
        saving={state.saving}
        error={
          state.error &&
          t(`manager/organizations:orgTabs.errors.${state.error}`)
        }
        copy={copy}
        labelFor={labelFor}
        onSave={(values: SettingsValues, lockVersion) =>
          run(group, () =>
            saveOrgGroup({ accountId: orgId, group, values, lockVersion })
          )
        }
        onReset={(lockVersion) =>
          run(group, () => resetOrgGroup(orgId, group, lockVersion))
        }
      />
    );
  };

  const designed = SECTION_GROUPS[section].filter(
    (g) => !CHECK_IN_GROUPS.includes(g)
  );
  const checkInGroups = SECTION_GROUPS[section].filter((g) =>
    CHECK_IN_GROUPS.includes(g)
  );
  const overridden = overriddenInRows(
    SECTION_GROUPS[section].flatMap((group) =>
      groups[group] ? [{ group, ...groups[group] }] : []
    ),
    {
      labelFor,
      groupLabel: (group) => t(`manager/organizations:orgTabs.groups.${group}`),
      notSet: t(key('notSet')),
      sameAsDefault: t(key('overriddenIn.sameAsDefault')),
    }
  );

  const forbidden =
    loadError === 'forbidden' ||
    (permissions !== null && !permissions.canViewSettings);

  return (
    <PageBody background="none">
      <TextTitle test-id="org-details-title">{orgName}</TextTitle>
      <OrganizationTabs active={section as OrgTab} permissions={permissions} />
      <Spacer size="large" />
      {forbidden ? (
        <NoAccessCard />
      ) : loadError ? (
        <TextTitle size="small" role="alert">
          {t(`manager/organizations:orgTabs.errors.${loadError}`)}
        </TextTitle>
      ) : permissions === null || Object.keys(groups).length === 0 ? (
        <Loader relative />
      ) : (
        <SettingsFormProvider copy={barCopy} useLeaveBlocker={useBlocker}>
          <Stack direction="column" gutter="large">
            <DefaultsBanner
              org={orgName ?? ''}
              section={section}
              viewOnly={!permissions.canManageSettings}
            />
            {designed.map((group) => card(group, false))}
            {checkInGroups.length > 1 ? (
              <CheckInCard title={t(key('checkIn.voiceTitle'))}>
                {checkInGroups.map((group) => card(group, true))}
              </CheckInCard>
            ) : (
              checkInGroups.map((group) => card(group, false))
            )}
            <OverriddenInTable org={orgName ?? ''} rows={overridden} />
          </Stack>
        </SettingsFormProvider>
      )}
    </PageBody>
  );
};
